import api from './client';
import type { ApiResponse } from '@gokrew/models/generic';
import type {
  CreateHandoverPayload,
  DocumentExpiringRow,
  HandoverPreview,
  HrExceptionView,
  ProbationEndingRow,
  TerminationPreview,
} from '@gokrew/models/handovers';

/* ---------------------------------- */
/* 🔧 Endpoints */
/* ---------------------------------- */

const endpoints = {
  handovers: (companyId: string) => `/companies/${companyId}/handovers`,
  handoverPreview: (companyId: string) =>
    `/companies/${companyId}/handovers/preview`,
  terminationPreview: (companyId: string, employeeId: string) =>
    `/companies/${companyId}/employees/${employeeId}/terminations/preview`,
  exceptions: (companyId: string) =>
    `/companies/${companyId}/approvals/exceptions`,
  probationEnding: (companyId: string) =>
    `/companies/${companyId}/approvals/exceptions/probation-ending`,
  documentsExpiring: (companyId: string) =>
    `/companies/${companyId}/approvals/exceptions/documents-expiring`,
};

/* ---------------------------------- */
/* 🚪 Departure handover */
/* ---------------------------------- */

/** What a leaver hands over: reports, approvals, work (termination flow). */
export const getTerminationPreview = async (
  companyId: string,
  employeeId: string
) => {
  const { data } = await api.get<ApiResponse<TerminationPreview>>(
    endpoints.terminationPreview(companyId, employeeId)
  );
  return data;
};

/** What a person hands over without leaving (the "Hand over their team" flow). */
export const getHandoverPreview = async (
  companyId: string,
  employeeId: string
) => {
  const { data } = await api.get<ApiResponse<HandoverPreview>>(
    endpoints.handoverPreview(companyId),
    { params: { employeeId } }
  );
  return data;
};

/** Plans a handover; one effective now runs straight away. */
export const createHandover = async (
  companyId: string,
  payload: CreateHandoverPayload
) => {
  const { data } = await api.post<ApiResponse<{ id: string }>>(
    endpoints.handovers(companyId),
    payload
  );
  return data;
};

/* ---------------------------------- */
/* ⚠️ HR exception view */
/* ---------------------------------- */

export const getHandoverExceptions = async (companyId: string) => {
  const { data } = await api.get<ApiResponse<HrExceptionView>>(
    endpoints.exceptions(companyId)
  );
  return data;
};

/** Probation ending in the next 14 days (needs `employee.read`). */
export const getProbationEnding = async (companyId: string) => {
  const { data } = await api.get<ApiResponse<ProbationEndingRow[]>>(
    endpoints.probationEnding(companyId)
  );
  return data;
};

/** Documents expired or expiring in 30 days (needs `employee_document.read`). */
export const getDocumentsExpiring = async (companyId: string) => {
  const { data } = await api.get<ApiResponse<DocumentExpiringRow[]>>(
    endpoints.documentsExpiring(companyId)
  );
  return data;
};
